import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { apiUrl } from 'app/common/url-resolver.service';
import { EMPTY, Observable } from 'rxjs';
import { Ajax } from 'app/common/ajax';
import { catchError, map } from 'rxjs/operators';
import { InquirySearch } from './inquiry';
import { BInteraction } from '../interaction/interaction';
import { SearchItemModel } from './search.model';
import { Operator } from 'app/common/common/enums/search.enum';
import { Helpers } from '@mi-tool/utils/helpers';
import { ExportModel } from './export.model';
import { BAttachment } from '../data-model.module';
import { GenericDialogService } from 'app/common/common/generic-dialog/generic-dialog.service';
import { MessageHandlerService } from 'app/common/common/message-handler/message-handler.service';
import { downloadExcel } from 'app/common/util/dom';

@Injectable({ providedIn: 'root' })
export class InquiriesService {
  private readonly URL = apiUrl('inquiries/');
  private readonly URL_SHARE = apiUrl('inquiries', 'share');
  private readonly URL_SEARCH = apiUrl('inquiries', 'search');
  private readonly URL_SAVED_SEARCH = apiUrl('inquiries', 'saved_search');
  private readonly URL_FILTER = apiUrl('inquiries', 'filter');
  private readonly URL_EXPORT = apiUrl('inquiries', 'export');
  private readonly URL_CUSTOM_FIELDS = apiUrl('inquiries', 'custom-fields');

  constructor(
    private http: HttpClient,
    private helperService: Helpers,
    private genericDialog: GenericDialogService,
    private messageService: MessageHandlerService
  ) {}

  createQuestionFromFU(historyId: number): Observable<void> {
    return this.http.post<void>(this.URL, {
      historyId,
    });
  }

  copyAnswerFrom(inquiryId: number, isDraft: boolean, copyFromInquiryId: number): Observable<void> {
    return this.http.put<void>(this.URL, {
      inquiryId: inquiryId,
      isDraft: isDraft,
      copyFromInquiryId: copyFromInquiryId,
    });
  }

  getSingleSearch(id: number): Observable<InquirySearch> {
    return this.http
      .get<any>(this.URL_SAVED_SEARCH + '/' + id)
      .pipe(map((resp) => InquirySearch.fromRest(resp)));
  }

  getSearches(): Observable<InquirySearch[]> {
    return this.http
      .get<any>(this.URL_SEARCH, Ajax.X_NO_LOADING_OPTIONS)
      .pipe(map((resp) => InquirySearch.fromRestArray(resp)));
  }

  search(
    inquiryFilter: InquirySearch,
    params?: { pageSize?: number; page?: number; orderBy?: string }
  ): Observable<any> {
    const queryParams = new Ajax.QueryParams()
      .add('page_size', params.pageSize || 10)
      .add('order_by', params.orderBy || '-created_ts')
      .add('page', params.page || 1);
    const body = this.getSearchBody(inquiryFilter);

    return this.http
      .post<any>(this.URL_FILTER + queryParams, { filters: body }, Ajax.X_NO_LOADING_OPTIONS)
      .pipe(
        map((resp) => {
          resp.result.map((i) => {
            i.landingInteraction = BInteraction.fromRest(i._object);
            return i;
          });
          return resp;
        })
      );
  }

  private getSearchBody(inquiryFilter: InquirySearch | any): SearchItemModel[] {
    let result: SearchItemModel[] = [];
    let data = inquiryFilter.advancedSearch || inquiryFilter.advanced_search || [];
    data
      .filter(
        (item) =>
          Number.isInteger(item.value) ||
          (item.value && item.value.length) ||
          (item.value && Object.keys(item.value).length)
      )
      .forEach((search) => {
        // We need to adapt date filters so we can send the correct data.
        switch (search.field) {
          case 'answer_date':
          case 'closed_on_date':
          case 'created_on':
          case 'due_date':
          case 'follow_up_received_date':
          case 'follow_up_sent_date':
          case 'internal_communication_date':
          case 'edited_on':
          case 'reopened_date':
            const dateTimeObject = this.helperService.getDateObject(search.value);
            result.push({
              filter_name: search.field,
              filter_values: [dateTimeObject.from, dateTimeObject.to],
              operator: search.join || Operator.And,
            });
            break;
          default:
            result.push({
              filter_name: search.field,
              filter_values: Array.isArray(search.value) ? search.value : [search.value],
              operator: search.join || Operator.And,
            });
            break;
        }
      });
    return result;
  }

  shareSearch(link: string, users: string[]): Observable<any> {
    return this.http
      .post<any>(this.URL_SHARE, { link, users }, Ajax.X_NO_LOADING_OPTIONS)
      .pipe(map((resp) => resp));
  }

  postSearch(inquiryFilter: InquirySearch): Observable<InquirySearch> {
    return this.http
      .post<any>(this.URL_SEARCH, inquiryFilter, Ajax.X_NO_LOADING_OPTIONS)
      .pipe(map((resp) => InquirySearch.fromRest(resp)));
  }

  updateSearch(inquiryFilter: InquirySearch): Observable<InquirySearch> {
    return this.http
      .put<any>(this.URL_SEARCH + '/' + inquiryFilter.id, inquiryFilter, Ajax.X_NO_LOADING_OPTIONS)
      .pipe(map((resp) => InquirySearch.fromRest(resp)));
  }

  deleteAllSearches(): Observable<any> {
    return this.http
      .delete<any>(this.URL_SEARCH, Ajax.X_NO_LOADING_OPTIONS)
      .pipe(map((resp) => resp));
  }

  deleteSearch(inquiryFilter: InquirySearch): Observable<InquirySearch> {
    return this.http
      .delete<any>(this.URL_SEARCH + '/' + inquiryFilter.id)
      .pipe(map((resp) => resp));
  }

  exportInquiry(inquiryFilter: InquirySearch): Observable<void> {
    return this.export(this.URL_EXPORT, inquiryFilter);
  }

  getCustomFields(inquiryId: number, isDraft: boolean): Observable<BCustomField[]> {
    return this.http
      .get<BCustomField[]>(this.URL_CUSTOM_FIELDS, {
        headers: Ajax.X_NO_LOADING_HEADERS,
        params: { inquiryId, isDraft },
      })
      .pipe(map(BCustomField.fromRestArray));
  }

  saveCustomField(change: CustomFieldChange): Observable<BCustomField> {
    return this.http
      .post<BCustomField>(this.URL_CUSTOM_FIELDS, change, Ajax.X_NO_LOADING_OPTIONS)
      .pipe(map((json) => json && BCustomField.fromRest(json)));
  }

  private export(url: string, filters: InquirySearch): Observable<void> {
    const body = this.getExportBody(filters);
    return this.http
      .post<Blob>(url, body, { responseType: 'blob' as 'json' })
      .pipe(
        map((response) => {
          if (response && response.size) {
            downloadExcel(response, 'inquiries');
          } else {
            this.messageService.info('NOTHING_EXPORT');
          }
        })
      )
      .pipe(
        catchError((error) => {
          if (error.status === 409) {
            this.genericDialog.showMaxDownloadsError();
          } else {
            this.messageService.httpError('Excel Export', error);
          }
          return EMPTY;
        })
      );
  }

  private getExportBody(inquiryFilter: InquirySearch): ExportModel {
    const result: ExportModel = {
      export_columns: Object.keys(
        inquiryFilter.exportedColumns || (inquiryFilter as any).exported_columns || {}
      ),
      filters: this.getSearchBody(inquiryFilter),
    };

    return result;
  }
}

export type CustomFieldChange = {
  inquiryId: number;
  isDraft: boolean;
  teamFieldId: number;
  value: string;
};

export class BCustomField {
  inquiryId: number;
  fieldType: string;
  label: string;
  value: string;
  teamCustomFieldId: number;
  attachment: BAttachment;

  static fromRest(json: any): BCustomField {
    json.attachment = json.attachment && BAttachment.fromRest(json.attachment);
    return Object.assign(new BCustomField(), json);
  }

  static fromRestArray(json: any[]): BCustomField[] {
    return json && json.map((v) => BCustomField.fromRest(v));
  }
}
